import { Card } from "@tine/designsystem-ui-react";
import type { Entry } from "contentful";
import { observer } from "mobx-react-lite";
import { useLayoutEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Link } from "wouter";
import { sendSelectTeaser, sendViewTeaser } from "../../common/tracking";
import type { ContentfulTeaser } from "../../common/types/cmsTypes";
import type { TypeImageWithFocusSkeleton } from "../../common/types/contentful";
import { isExternalLink } from "../../common/utils";
import { isArticle, isImageLink, isImageLinkToUrl } from "../../common/utils/cmsListUtils";
import { getModuleVisibility } from "../../common/utils/segmentUtils";
import contentStore from "../../stores/cms/contentStore";
import segmentStore from "../../stores/cms/segmentStore";
import ImageWithFocus from "./ImageWithFocus";

interface ContentCardLinkProps {
   entry: ContentfulTeaser;
   trackingListName?: string;
}

const ContentCardLink = ({ entry, trackingListName = "contentCardLink" }: ContentCardLinkProps) => {
   const [cardWidth, setCardWidth] = useState<number>(0);
   const cardRef = useRef<HTMLDivElement | null>(null);

   const id = entry.sys.id;
   const type = isArticle(entry) ? "article" : "url";
   const title = isArticle(entry) ? (entry.fields.callToAction ?? entry.fields.title) : (entry.fields.text ?? "");
   const description = isArticle(entry) ? (entry.fields.preface ?? "") : (entry.fields.caption ?? "");
   const image = isArticle(entry)
      ? entry.fields.headerImage
      : (entry.fields.image as Entry<TypeImageWithFocusSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>);

   const hasImage = !!image?.fields.image;

   let url = "";
   if (isImageLinkToUrl(entry)) {
      url = entry.fields.url;
   } else {
      const entryId = isImageLink(entry) ? entry.fields.entry?.sys.id : entry.sys.id;
      const urlData = contentStore.urlData.data.find((urlData) => urlData.id === entryId);
      url = urlData?.fullUrl ? `/${urlData.fullUrl}` : "#";
   }

   const trackImpression = (isVisible: boolean) => {
      if (isVisible) {
         sendViewTeaser(title, type, trackingListName);
      }
   };

   const combinedRef = (el: HTMLDivElement | null) => {
      cardRef.current = el;
      viewRef(el);
   };

   useLayoutEffect(() => {
      if (!cardRef.current) return;
      const node = cardRef.current;
      setCardWidth(node.offsetWidth);
      const resizeObserver = new window.ResizeObserver((entries) => {
         for (const entry of entries) {
            if (entry.target === node && entry.contentRect) {
               setCardWidth(entry.contentRect.width);
            }
         }
      });
      resizeObserver.observe(node);
      return () => resizeObserver.disconnect();
   }, [cardRef.current]);

   const { ref: viewRef } = useInView({
      triggerOnce: true,
      onChange: trackImpression
   });

   function handleClickImageLink() {
      sendSelectTeaser(title, type, trackingListName, url);
   }

   if (isArticle(entry) && !segmentStore.isArticleVisible(id)) {
      return null;
   }

   if (isImageLink(entry)) {
      if (!entry.fields.entry) {
         return null;
      }
      if (!segmentStore.isArticleVisible(entry.fields.entry.sys.id)) {
         return null;
      }
   }

   if (isImageLinkToUrl(entry) && !getModuleVisibility(entry, segmentStore.currentCustomerProperties)) {
      return null;
   }

   const imageContent = hasImage ? (
      <ImageWithFocus
         width={800}
         ratio="16x9"
         entryId={image?.sys.id || entry.sys.id}
         imageFromParent={image}
         placement="aboveFold"
         className="transition-transform duration-300 group-hover:scale-105"
      />
   ) : (
      <div className="bg-base-100 w-full h-full aspect-[16/9]" />
   );

   return (
      // biome-ignore lint/a11y/noStaticElementInteractions: should be a button
      <div ref={combinedRef} className="w-full" onClick={handleClickImageLink}>
         {cardWidth >= 640 ? (
            <Card
               to={url}
               href={url}
               as={isExternalLink(url) ? "a" : Link}
               className="flex flex-col h-full"
               borderVariant="shadow"
            >
               <Card.Layout direction="horizontal" className="flex-1 [&_img]:rounded-none">
                  <div className="w-1/2 overflow-hidden">{imageContent}</div>
                  <Card.Content className="justify-center @sm:p-8">
                     <Card.Title className="line-clamp-none">{title}</Card.Title>
                     <Card.Description>{description}</Card.Description>
                  </Card.Content>
               </Card.Layout>
            </Card>
         ) : (
            <Card
               to={url}
               href={url}
               as={isExternalLink(url) ? "a" : Link}
               className="flex flex-col h-full"
               borderVariant="shadow"
            >
               <Card.Layout direction="vertical" className="flex-1 [&_img]:rounded-none">
                  <div className="overflow-hidden">{imageContent}</div>
                  <Card.Content className="@sm:p-8">
                     <Card.Title className="line-clamp-none">{title}</Card.Title>
                     <Card.Description>{description}</Card.Description>
                  </Card.Content>
               </Card.Layout>
            </Card>
         )}
      </div>
   );
};

export default observer(ContentCardLink);
